import { useRef, useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CloseIcon from '@mui/icons-material/Close'
import {
  formatProofFileSize,
  proofFileTypeLabel,
  validateProofOfDeliverySelection,
  POD_MAX_FILES,
} from '../utils/proofOfDelivery'

function ProofOfDelivery({
  shipmentId,
  files = [],
  disabled = false,
  uploading = false,
  onUpload,
  onDelete,
}) {
  const inputRef = useRef(null)
  const [validationError, setValidationError] = useState(null)
  const [previewFile, setPreviewFile] = useState(null)
  const busy = disabled || uploading
  const attached = files.length > 0
  const previewMime = String(previewFile?.mime_type || '').toLowerCase()
  const previewIsImage = previewMime.startsWith('image/')
  const previewIsPdf = previewMime === 'application/pdf'

  const handlePick = (event) => {
    const picked = Array.from(event.target.files || [])
    event.target.value = ''
    if (!picked.length || busy) return
    const message = validateProofOfDeliverySelection(files, picked)
    if (message) {
      setValidationError(message)
      return
    }
    setValidationError(null)
    onUpload?.(shipmentId, picked)
  }

  const handleDelete = (fileId) => {
    if (busy || !onDelete) return
    setValidationError(null)
    onDelete(shipmentId, fileId)
  }

  return (
    <Box sx={{ minWidth: 0, flex: '1 1 260px', maxWidth: { sm: 420 } }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Proof of Delivery
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {attached ? 'Proof of delivery attached' : 'Proof of delivery not attached'}
      </Typography>
      {validationError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setValidationError(null)}>
          {validationError}
        </Alert>
      )}
      {files.map((file) => (
        <Box
          key={file.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 0.5,
            minWidth: 0,
          }}
        >
          <IconButton
            size="small"
            aria-label={`Preview ${file.file_name || 'proof of delivery file'}`}
            disabled={!file.url}
            onClick={() => setPreviewFile(file)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" noWrap title={file.file_name}>
              {file.file_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {proofFileTypeLabel(file)} · {formatProofFileSize(file.size_bytes)}
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label={`Delete ${file.file_name || 'proof of delivery file'}`}
            disabled={busy}
            onClick={() => handleDelete(file.id)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <input
        ref={inputRef}
        type="file"
        hidden
        multiple
        accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
        onChange={handlePick}
      />
      <Button
        variant="outlined"
        size="small"
        startIcon={<CloudUploadIcon />}
        disabled={busy || files.length >= POD_MAX_FILES}
        onClick={() => inputRef.current?.click()}
        sx={{ mt: 0.5 }}
      >
        {uploading ? 'Uploading…' : 'Upload'}
      </Button>
      <Dialog
        open={Boolean(previewFile)}
        onClose={() => setPreviewFile(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh', m: 2 },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
            {previewFile?.file_name || 'Proof of delivery'}
          </Typography>
          <IconButton
            aria-label="Close preview"
            onClick={() => setPreviewFile(null)}
            sx={{ color: '#666' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: previewIsImage ? '#000' : '#f5f5f5',
            minHeight: 240,
          }}
        >
          {previewIsImage && previewFile?.url && (
            <img
              src={previewFile.url}
              alt={previewFile.file_name || 'Proof of delivery'}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
              }}
            />
          )}
          {previewIsPdf && previewFile?.url && (
            <Box
              component="iframe"
              title={previewFile.file_name || 'Proof of delivery PDF'}
              src={previewFile.url}
              sx={{ width: '100%', height: '70vh', border: 0, bgcolor: '#fff' }}
            />
          )}
          {previewFile && !previewIsImage && !previewIsPdf && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
              Preview is not available for this file.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8f9fb' }}>
          <Button onClick={() => setPreviewFile(null)} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ProofOfDelivery
